import React from 'react';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import Icon from '@material-ui/core/Icon';
import Input from '@material-ui/core/Input';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { match, RouteComponentProps } from 'react-router-dom';

import GoogleMapReact, { Coords } from 'google-map-react';
// Components
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import EnhancedForm from '../../../components/form';
import { FormDatas, FormPickerDatas } from '../../../local-types';
import { TooltipButton } from '../../../components/tooltip-button';
import Listing from '../../../components/listing';
// Modules
import { ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import {
    actions as companyActions,
    selectors as companySelectors,
    types as companyTypes,
} from '../../../redux/reducers/companies';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations';
import { Company, CompanyById, StopType, TParams } from '../../../redux/reducers/api-types';
// Ressources
import FormUtils from '../../../utils/forms';
import withGlobalStyles from '../../../utils/global-styles';
import ObjectUtils from '../../../utils/objectUtils';
import { ChangeEvent, FormEvent } from '../../../utils/typeUtils';
import DateUtils from '../../../utils/dateUtils';
import UserUtils, { AccountRight, RightRank } from '../../../utils/userUtils';
import MapUtils from '../../../utils/mapUtils';
import { STOP_TYPE_COLOR } from '../view/company-view';
import { paths } from '../../paths';
import { clsx } from '../../../utils/htmlUtils';
import { formDefaultConf } from '../../pricing/managePrices/shared';
import envConfig from '../../../config';

const DEFAULT_RADIUS = 5000;

interface CompanyAddRestrictionProps extends WithTranslation, RouteComponentProps {
    match: match<TParams>;
    globalClasses: any;
    center: Coords;
    zoom: number;
    companies: CompanyById;
    getCompanyError: ErrorPayload;
    getTravelPoliciesError: ErrorPayload;
    createTravelPolicyError: ErrorPayload;
    isCreatingTravelPolicy: boolean;
    getCompany: (id: string, callback: Function) => any;
    getTravelPolicies: (uuid, id, parentUuid?) => any;
    createTravelPolicy: (uuid, id, payload) => any;
}

interface CompanyAddRestrictionState {
    formData: any;
    formMinPrice: any;
    formMinPriceInit: any;
    formPoint: any;
}

class CompanyAddRestrictionPage extends React.Component<CompanyAddRestrictionProps, CompanyAddRestrictionState> {
    static defaultProps = {
        center: MapUtils.defaultCenter,
        zoom: MapUtils.defaultZoom,
    };

    map: any;
    maps: any;
    markers: any[] = [];
    defaultBounds: any;
    markerHint: any;
    circles: any[] = [];

    optionsStopType: FormPickerDatas[];

    isAdmin: boolean;

    constructor(props: CompanyAddRestrictionProps) {
        super(props);
        this.isAdmin = UserUtils.checkRightsOrAdmin({ name: AccountRight.COMPANY_SETTINGS, mode: RightRank.W });
        this.state = {
            formMinPrice: ObjectUtils.deepCopy(formDefaultConf),
            formMinPriceInit: ObjectUtils.deepCopy(formDefaultConf),
            formData: {
                areas: [],
                time_slots: [],
            },
            formPoint: {
                lat: null,
                lng: null,
                stop_type: '',
                radius: DEFAULT_RADIUS,
            },
        };

        this.optionsStopType = [
            {
                id: '',
                name: `${StopType.PICKUP} & ${StopType.DROP_OFF}`,
            },
            {
                id: StopType.PICKUP,
                name: StopType.PICKUP,
            },
            {
                id: StopType.DROP_OFF,
                name: StopType.DROP_OFF,
            },
        ];
    }

    componentDidMount() {
        const { companies, getCompany, getTravelPolicies, match } = this.props;
        const id = match.params.id;
        if (!companies || !companies[id]) {
            getCompany(id, (dispatch, response) => {
                const company: Company = response?.payload;
                getTravelPolicies(company?.uuid, id, company?.parent?.uuid);
            });
        } else {
            const company: Company = companies[id];
            getTravelPolicies(company.uuid, id, company?.parent?.uuid);
        }
    }

    static getDerivedStateFromProps(props: CompanyAddRestrictionProps, state: CompanyAddRestrictionState) {
        const { companies, match } = props;
        const id = match.params.id;
        const { formData } = state;

        if (!formData.loaded && companies?.[id]?.travel_policies) {
            const formatedData: any = ObjectUtils.deepCopy(companies[id].travel_policies);
            formatedData.time_slots = CompanyAddRestrictionPage.orderArrayFromServer(formatedData.time_slots);

            // console.log('=>', formatedData);

            formatedData.loaded = true;
            return { formData: formatedData };
        }
        return null;
    }

    static orderArrayFromServer = (array): Array<any> => {
        const res = [];
        if (!array) {
            return res;
        }
        Object.entries(array).forEach(([day, slots]: [string, Array<any>]) => {
            slots.forEach((s) => {
                const idx = res.find((o) => o.start === s.start && o.end === s.end);
                if (!idx) {
                    res.push({
                        start: s.start,
                        end: s.end,
                        days: [{ short: day, value: true }],
                    });
                } else {
                    idx.days.push({ short: day, value: true });
                }
            });
        });
        return res;
    };

    static orderArrayToServer = (array): any => {
        const res = {};
        if (!array) {
            return res;
        }
        array.forEach((v) => {
            v.days
                .filter((d) => d.value)
                .forEach(({ short, value }) => {
                    const slotdx = Object.keys(res).find((o) => o === short);
                    if (!slotdx) {
                        res[short] = [{ start: v.start, end: v.end }];
                    } else {
                        res[slotdx].push({ start: v.start, end: v.end });
                    }
                });
        });
        return res;
    };

    //===================================//
    //=============== Map ===============//
    //===================================//

    handleApiLoaded = (map, maps) => {
        this.map = map;
        this.maps = maps;

        const { center } = this.props;
        const { formData } = this.state;
        const nextFormData = { ...formData } as any;

        this.defaultBounds = MapUtils.getBounds(maps, [center]);

        // Create current areas circles
        const { match, companies } = this.props;
        const id = match.params.id;
        const company = companies[id];

        if (company?.travel_policies?.areas?.length > 0) {
            const bounds = new maps.LatLngBounds();
            company.travel_policies.areas.forEach((a) => {
                const colors = STOP_TYPE_COLOR[a.stop_type] ?? STOP_TYPE_COLOR.all;
                const newCircle = MapUtils.generateCircle(
                    maps,
                    +a.latitude,
                    +a.longitude,
                    +a.radius,
                    colors.strokeColor,
                    colors.fillColor,
                    true,
                );
                maps.event.addListener(newCircle, 'center_changed', (e) => this.onCenterChanged(e, newCircle));
                maps.event.addListener(newCircle, 'radius_changed', (e) => this.onRadiusChanged(e, newCircle));
                this.circles.push(newCircle);
                newCircle.setMap(map);
                MapUtils.extendCircleBounds(maps, bounds, +a.latitude, +a.longitude, +a.radius);
            });
            map.fitBounds(bounds);
            this.defaultBounds = bounds;
        }

        maps.event.addListener(
            map,
            'click',
            function (e) {
                this.onClickHandler(e, maps, map, nextFormData);
            }.bind(this),
        );
    };

    onCenterChanged = (e, newCircle) => {
        const index = this.circles.findIndex((c) => c === newCircle);

        const { formData } = this.state;
        const circleEdit = formData.areas[index];
        if (circleEdit) {
            circleEdit.latitude = newCircle.center.lat();
            circleEdit.longitude = newCircle.center.lng();

            this.setState({
                formData,
            });
        }
    };

    onRadiusChanged = (e, newCircle) => {
        const index = this.circles.findIndex((c) => c === newCircle);

        const { formData } = this.state;
        const circleEdit = formData.areas[index];
        if (circleEdit) {
            circleEdit.radius = newCircle.getRadius();

            this.setState({
                formData,
            });
        }
    };

    onShowMarker = (p) => (e) => {
        const { maps, map } = this;
        if (!this.markerHint) {
            this.markerHint = MapUtils.addMarker(maps, map, p);
        }
    };

    onHideMarker = () => (e) => {
        MapUtils.removeMarker(this.markerHint);
        this.markerHint = null;
    };

    onClickHandler(e, maps, map, formData) {
        const bounds = this.defaultBounds;

        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        const radius = this.state.formPoint.radius ?? 500;
        const stop_type = this.state.formPoint.stop_type;

        const colors = STOP_TYPE_COLOR[stop_type] ?? STOP_TYPE_COLOR.all;

        const newCircle = MapUtils.generateCircle(
            maps,
            +lat,
            +lng,
            +radius,
            colors.strokeColor,
            colors.fillColor,
            true,
        );
        maps.event.addListener(newCircle, 'center_changed', (event) => this.onCenterChanged(event, newCircle));
        maps.event.addListener(newCircle, 'radius_changed', (event) => this.onRadiusChanged(event, newCircle));
        this.circles.push(newCircle);
        newCircle.setMap(map);
        MapUtils.extendCircleBounds(maps, bounds, +lat, +lng, +radius);

        if (!formData.areas) {
            formData.areas = [];
        }

        const payload: any = { latitude: lat, longitude: lng, radius, stop_type: stop_type ? stop_type : null };
        formData.areas.push(Object.assign({}, payload));

        this.setState({
            formData,
        });
    }

    //========================================//
    //=============== Handlers ===============//
    //========================================//

    handleChipsActivate = (index) => (e) => {
        e.stopPropagation();
        e.preventDefault();
        const { formMinPrice } = this.state;
        formMinPrice.days[index].value = !formMinPrice.days[index].value;
        this.setState({
            formMinPrice,
        });
    };

    handleChange = (event: ChangeEvent) => {
        const { formMinPrice } = this.state;
        this.setState({
            formMinPrice: FormUtils.nextSubState(event, formMinPrice),
        });
    };

    handleDelete = (index) => (e) => {
        const { formData } = this.state;

        formData.time_slots.splice(index, 1);
        this.setState({
            formData,
        });
    };

    handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        const { formData, formMinPrice, formMinPriceInit } = this.state;

        if (!formData.time_slots) {
            formData.time_slots = [];
        }
        formData.time_slots.push(Object.assign({}, formMinPrice));

        this.setState({
            formData,
            formMinPrice: Object.assign({}, formMinPriceInit),
        });
    };

    handleReset = () => {
        const { formData } = this.state;
        formData.time_slots = [];
        formData.areas = [];

        this.circles.forEach((circle) => circle.setMap(null));
        this.circles = [];

        this.setState({
            formData,
        });
    };

    handleDeletePoint = (index, point) => (e) => {
        const { formData } = this.state;
        const nextFormData = { ...formData };

        const circle = this.circles[index];
        circle.setMap(null);
        this.circles.splice(index, 1);
        nextFormData.areas.splice(index, 1);

        this.setState({
            formData: nextFormData,
        });
    };

    handleChangePoint = (event) => {
        const { formPoint } = this.state;
        this.setState({
            formPoint: FormUtils.nextSubState(event, formPoint),
        });
    };

    handleSubmitPoint = (event) => {
        const { formData, formPoint } = this.state;
        event.preventDefault();

        if (this.maps && this.map) {
            event.latLng = new this.maps.LatLng(formPoint.lat, formPoint.lng);
            this.onClickHandler(event, this.maps, this.map, formData);

            this.setState({
                formPoint: { lat: null, lng: null, radius: formPoint.radius, stop_type: formPoint.stop_type },
            });
        }
    };

    //========================================//
    //=============== Builders ===============//
    //========================================//

    renderFormPoint() {
        const { t } = this.props;
        const { formPoint } = this.state;
        const form = 'form_point';

        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 20,
                }}
            >
                <FormControl key={'lat'} margin="dense">
                    <span style={{ margin: 'auto' }}>{t('common:coord.lat')}</span>
                    <Input
                        id={'lat'}
                        type={'number'}
                        inputProps={{ form }}
                        value={formPoint.lat ?? ''}
                        name={`lat`}
                        required={true}
                        onChange={(e) => this.handleChangePoint(e)}
                    />
                </FormControl>
                <FormControl key={'lng'} margin="dense">
                    <span style={{ margin: 'auto' }}>{t('common:coord.lng')}</span>
                    <Input
                        id={'lng'}
                        type={'number'}
                        inputProps={{ form }}
                        value={formPoint.lng ?? ''}
                        name={`lng`}
                        required={true}
                        onChange={(e) => this.handleChangePoint(e)}
                    />
                </FormControl>
                <FormControl key={'radius'} margin="dense">
                    <span style={{ margin: 'auto' }}>{t('common:coord.radius')}</span>
                    <Input
                        id={'radius'}
                        type={'number'}
                        inputProps={{ form }}
                        value={formPoint.radius ?? ''}
                        name={`radius`}
                        required={true}
                        onChange={(e) => this.handleChangePoint(e)}
                    />
                </FormControl>
                <FormControl>
                    <span style={{ margin: 'auto' }}>{t('common:coord.stop_type')}</span>
                    <Select
                        native
                        id={'stop_type'}
                        name={'stop_type'}
                        value={formPoint.stop_type ?? ''}
                        inputProps={{ form }}
                        variant="outlined"
                        required={true}
                        disabled={false}
                        onChange={(e) => this.handleChangePoint(e)}
                    >
                        {this.optionsStopType.map((select, i) => {
                            return (
                                <option key={select.id} value={select.id}>
                                    {select.name}
                                </option>
                            );
                        })}
                    </Select>
                </FormControl>
                <TooltipButton title={t('common:add')}>
                    <Button
                        aria-label="add_point"
                        type="submit"
                        variant="contained"
                        form={form}
                        color={
                            formPoint.lat !== null && formPoint.lng !== null && formPoint.radius !== null
                                ? 'secondary'
                                : 'inherit'
                        }
                    >
                        <Icon fontSize="small">add</Icon>
                    </Button>
                </TooltipButton>
            </div>
        );
    }

    generateInputs = (): FormDatas[] => {
        const { t, center, zoom } = this.props;
        const inputs: FormDatas[] = [
            {
                title: t('trip_policy_restrictions.hours'),
                sectionStyle: { flex: 1 },
                datas: [
                    {
                        id: 'time_slots',
                        label: t('trip_policy_restrictions.hours'),
                        type: 'create',
                        customView: (val, handleChange) => {
                            const { formData, formMinPrice } = this.state;
                            const { globalClasses } = this.props;
                            return (
                                <div>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ display: 'flex', margin: 'auto', marginBottom: 30 }}>
                                            {DateUtils.generateDays(t).map((d, index) => {
                                                const activated = formMinPrice.days[index].value;
                                                return (
                                                    <Chip
                                                        key={d.id}
                                                        icon={null}
                                                        color={activated ? 'secondary' : 'default'}
                                                        label={d.label}
                                                        onClick={this.handleChipsActivate(index)}
                                                        style={{ marginRight: 8 }}
                                                        // className={classes.chip}
                                                    />
                                                );
                                            })}
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                marginBottom: 50,
                                            }}
                                        >
                                            <span style={{ margin: 'auto' }}>{t('common:from')}</span>
                                            <FormControl key={'start'} margin="dense">
                                                <Input
                                                    id={'start'}
                                                    type={'time'}
                                                    inputProps={{ form: 'form_hours' }}
                                                    placeholder={'_:_'}
                                                    value={formMinPrice.start ?? ''}
                                                    name={`start`}
                                                    required={true}
                                                    onChange={this.handleChange}
                                                />
                                            </FormControl>
                                            <span style={{ margin: 'auto' }}>{t('common:hour_to')}</span>
                                            <FormControl key={'end'} margin="dense">
                                                <Input
                                                    id={'end'}
                                                    inputProps={{ form: 'form_hours' }}
                                                    type={'time'}
                                                    placeholder={'_:_'}
                                                    value={formMinPrice.end ?? ''}
                                                    name={`end`}
                                                    required={true}
                                                    onChange={this.handleChange}
                                                />
                                            </FormControl>
                                            <TooltipButton title={t('common:add')}>
                                                <Button
                                                    aria-label="filter list"
                                                    type="submit"
                                                    variant="contained"
                                                    form="form_hours"
                                                    color={
                                                        formMinPrice.start && formMinPrice.end ? 'secondary' : 'inherit'
                                                    }
                                                >
                                                    <Icon fontSize="small">add</Icon>
                                                </Button>
                                            </TooltipButton>
                                        </div>
                                    </div>
                                    <Listing
                                        datas={formData?.time_slots?.map(({ days, end, start, value }, index) => {
                                            const sortedDays = ObjectUtils.sortByDay(days);
                                            const strDays = sortedDays
                                                .filter((d) => d.value)
                                                .map(({ short }, dindex) => t('common:days.' + short))
                                                .join(', ');
                                            return (
                                                <div
                                                    key={`${value} ${strDays} ${start} ${end}`}
                                                    style={{
                                                        flex: 'inherit',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <div style={{ marginRight: 6, flex: 4 }}>
                                                        <b>{strDays}</b>
                                                        <br />
                                                        <span>
                                                            {`${t('common:hour_from')} ${start} 
                                                                    ${t('common:hour_to')} ${end}`}
                                                        </span>
                                                    </div>
                                                    {this.isAdmin && (
                                                        <TooltipButton title={t('common:delete')}>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="primary"
                                                                onClick={this.handleDelete(index)}
                                                                className={clsx(
                                                                    globalClasses.btnAutoWidth,
                                                                    globalClasses.btnDanger,
                                                                )}
                                                            >
                                                                <Icon fontSize="small">delete</Icon>
                                                            </Button>
                                                        </TooltipButton>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    />
                                </div>
                            );
                        },
                    },
                ],
            },
            {
                title: t('trip_policy_restrictions.location'),
                sectionStyle: { flex: 2 },
                datas: [
                    {
                        id: 'circles',
                        label: t('common:shape.circle'),
                        required: true,
                        customView: (val, handleChange) => {
                            const { formData } = this.state;
                            const { globalClasses } = this.props;
                            return (
                                <>
                                    <p>{t('trip_policy_restrictions.location_hint')}</p>
                                    {this.renderFormPoint()}
                                    <div style={{ display: 'flex', justifyContent: 'end' }}>
                                        {Object.entries(STOP_TYPE_COLOR).map((k, index) => {
                                            return (
                                                <div
                                                    key={`${index}-${k[1].label}`}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        marginRight: 8,
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            height: 10,
                                                            width: 10,
                                                            backgroundColor: k[1].fillColor,
                                                            marginRight: 8,
                                                        }}
                                                    />
                                                    {k[1].label}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            marginBottom: 16,
                                        }}
                                    >
                                        <div style={{ marginRight: 16 }}>
                                            <b>{t('common:shape.circle')}</b>
                                            <hr />
                                            {formData?.areas &&
                                                formData.areas.map((p, index) => (
                                                    <div key={`${p.latitude}${p.longitude}${p.radius}${index}`}>
                                                        <div
                                                            style={{ marginBottom: 4, display: 'flex' }}
                                                            onMouseOver={this.onShowMarker({
                                                                lat: +p.latitude,
                                                                lng: +p.longitude,
                                                            })}
                                                            onMouseLeave={this.onHideMarker()}
                                                        >
                                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                <i>
                                                                    {!p.stop_type || p.stop_type === ''
                                                                        ? `${StopType.PICKUP}&${StopType.DROP_OFF}`
                                                                        : p.stop_type}
                                                                </i>
                                                                <span style={{ marginRight: 8 }}>
                                                                    {`${(+p.latitude).toFixed(
                                                                        6,
                                                                    )} ${(+p.longitude).toFixed(
                                                                        6,
                                                                    )} ${(+p.radius).toFixed(2)}`}
                                                                </span>
                                                            </div>
                                                            <TooltipButton title={t('common:delete')}>
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    color="primary"
                                                                    style={{ marginRight: 8 }}
                                                                    onClick={this.handleDeletePoint(index, p)}
                                                                    className={clsx(
                                                                        globalClasses.btnAutoWidth,
                                                                        globalClasses.btnDanger,
                                                                    )}
                                                                >
                                                                    <Icon fontSize="small">delete</Icon>
                                                                </Button>
                                                            </TooltipButton>
                                                        </div>
                                                        <hr />
                                                    </div>
                                                ))}
                                        </div>
                                        <div style={{ flex: 1, minHeight: '50vh', width: '100%' }}>
                                            <GoogleMapReact
                                                bootstrapURLKeys={{ key: envConfig.google_key }}
                                                yesIWantToUseGoogleMapApiInternals
                                                onGoogleApiLoaded={({ map, maps }) => this.handleApiLoaded(map, maps)}
                                                defaultCenter={center}
                                                defaultZoom={zoom}
                                            />
                                        </div>
                                    </div>
                                </>
                            );
                        },
                    },
                ],
            },
        ];

        return inputs;
    };

    onSubmitTariff = (formData: any, event) => {
        if (!this.isAdmin) {
            return;
        }

        // Apply custom field to the formData
        formData.areas = this.state.formData.areas;
        formData.time_slots = this.state.formData.time_slots;

        this.setState({
            formData: formData,
        });

        const formatedData = ObjectUtils.deepCopy(formData);
        formatedData.time_slots = CompanyAddRestrictionPage.orderArrayToServer(formatedData.time_slots);

        const { companies, match } = this.props;
        const id: string = match.params.id;
        const company = companies[id];
        formatedData.company = { id: company.uuid };

        // console.log('sending', formatedData);

        this.props.createTravelPolicy(company.uuid, id, formatedData);
    };

    render() {
        const { formData } = this.state;
        const { t, match, getCompanyError, getTravelPoliciesError, createTravelPolicyError, isCreatingTravelPolicy } =
            this.props;

        const id: string = match.params.id;
        const formPayload = formData;

        return (
            <App needRights={{ name: AccountRight.COMPANY_SETTINGS, mode: RightRank.W }}>
                <ErrorMessage payload={getCompanyError || getTravelPoliciesError} />
                <ErrorMessage payload={createTravelPolicyError} />
                {formPayload?.loaded && (
                    <>
                        <form onSubmit={this.handleSubmit} id="form_hours">
                            <input type="submit" hidden value="Ok" />
                        </form>
                        <form onSubmit={this.handleSubmitPoint} id="form_point">
                            <input type="submit" hidden value="Ok" />
                        </form>
                        <EnhancedForm
                            payload={formPayload}
                            inputs={this.generateInputs()}
                            backRoute={`${paths.companies}/${id}`}
                            onReset={this.handleReset}
                            onSubmit={this.onSubmitTariff}
                            loader={isCreatingTravelPolicy}
                            readOnly={!this.isAdmin}
                            actionBarHidden={!this.isAdmin}
                        />
                    </>
                )}
            </App>
        );
    }
}

const CompanyAddRestrictionWithTransation = withGlobalStyles(
    withTranslation(['company', 'common'])(CompanyAddRestrictionPage),
);

const mapStateToProps = (state) => ({
    companies: companySelectors.getCompanies(state),
    getCompanyError: errorSelectors.getCompanyError(state),
    getTravelPoliciesError: errorSelectors.getTravelPoliciesError(state),
    createTravelPolicyError: errorSelectors.createTravelPolicyError(state),
    isCreatingTravelPolicy: pendingSelectors.isPending(state, companyTypes.CREATE_TRAVEL_POLICY),
});

const mapDispatchToProps = {
    getCompany: companyActions.getCompany,
    getTravelPolicies: companyActions.getTravelPolicies,
    createTravelPolicy: companyActions.createTravelPolicy,
};

export default connect(mapStateToProps, mapDispatchToProps)(CompanyAddRestrictionWithTransation);
